/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useSetting } from '@airasia-rn/account/hooks';
import {
  HOTEL_SEARCH_VIEW_FLAGS_DEFAULT,
  HOTELS_LISTING_FEATURE_FLAGS,
} from '../configs/featureFlagConstant';
import type { HotelListingFlags, HotelSearchViewFlags } from '../types';

type UseSearchViewFeatureFlagResult = HotelSearchViewFlags & {
  isSettingsLoading: boolean;
};

type MergedListingAndSearchFlags = HotelListingFlags &
  Partial<HotelSearchViewFlags>;

export const useSearchViewFeatureFlag = () => {
  const { value: listingValue, isSettingsLoading: isListingSettingsLoading } =
    useSetting(HOTELS_LISTING_FEATURE_FLAGS);

  const searchViewFlags = useMemo<HotelSearchViewFlags>(() => {
    if (!listingValue) {
      return HOTEL_SEARCH_VIEW_FLAGS_DEFAULT;
    }

    try {
      const sanitized = listingValue.replace(/,(\s*[}\]])/g, '$1');
      const parsedFlags = JSON.parse(sanitized);

      if (
        parsedFlags &&
        typeof parsedFlags === 'object' &&
        !Array.isArray(parsedFlags)
      ) {
        return {
          ...HOTEL_SEARCH_VIEW_FLAGS_DEFAULT,
          ...(parsedFlags as Partial<MergedListingAndSearchFlags>),
        };
      }

      return HOTEL_SEARCH_VIEW_FLAGS_DEFAULT;
    } catch {
      return HOTEL_SEARCH_VIEW_FLAGS_DEFAULT;
    }
  }, [listingValue]);

  return {
    ...searchViewFlags,
    isSettingsLoading: isListingSettingsLoading,
  } as UseSearchViewFeatureFlagResult;
};
